import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Wind } from "lucide-react";
import { api } from "@/lib/api";
import MapPanel from "@/components/MapPanel";
import TrendChart from "@/components/TrendChart";
import HotspotTrend from "@/components/HotspotTrend";
import IspuTrend from "@/components/IspuTrend";
import IspuDetail from "@/components/IspuDetail";
import AlertList from "@/components/AlertList";
import DistrictList from "@/components/DistrictList";

export default function PrintReport() {
  const [data, setData] = useState({ districts: [], alerts: [], hourly: [], summary: {}, source: "-" });
  const [hotspots, setHotspots] = useState([]);
  const [kalbarHotspots, setKalbarHotspots] = useState([]);
  const [trend, setTrend] = useState([]);
  const [ispuTrend, setIspuTrend] = useState({ items: [], districts: [] });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, h, k, t, it] = await Promise.all([
          api.get("/monitoring"),
          api.get("/hotspots", { params: { date_filter: "today_wib" } }),
          api.get("/hotspots/kalbar", { params: { date_filter: "today_wib" } }),
          api.get("/hotspots/trend"),
          api.get("/ispu/trend"),
        ]);
        setData(m.data);
        setHotspots(h.data.items || []);
        setKalbarHotspots(k.data.items || []);
        setTrend(t.data.items || []);
        setIspuTrend(it.data || { items: [], districts: [] });
      } catch (e) {
        console.error(e);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const districtsWithCounts = useMemo(() => {
    const byD = {};
    hotspots.forEach((h) => {
      byD[h.district] = (byD[h.district] || 0) + 1;
    });
    return data.districts
      .map((d) => ({ ...d, hotspot_count: byD[d.name] || 0 }))
      .sort((a, b) => b.hotspot_count - a.hotspot_count);
  }, [data.districts, hotspots]);

  const now = new Date();

  return (
    <main className="print-page" data-testid="print-report-page">
      <div className="print-toolbar">
        <Link to="/admin" className="admin-button" data-testid="print-back">
          <ArrowLeft size={15} /> Kembali ke Panel Admin
        </Link>
        <span>
          Pratinjau laporan · gunakan tombol cetak lalu pilih <b>"Simpan sebagai PDF"</b>.
          Aktifkan <b>"Background graphics"</b> pada opsi cetak.
        </span>
        <button
          className="admin-primary print-btn"
          onClick={() => window.print()}
          disabled={!ready}
          data-testid="print-pdf-button"
        >
          <Printer size={15} /> Cetak / Simpan PDF
        </button>
      </div>

      <header className="print-head" data-testid="print-head">
        <img src="/bpbd-logo.png" alt="Logo BPBD" />
        <div>
          <b>LAPORAN DASHBOARD SITAKO</b>
          <span>BPBD Kabupaten Kayong Utara · Sistem Informasi Terpadu Antisipasi Kebencanaan Online</span>
          <small>
            Dicetak:{" "}
            {now.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}{" "}
            {now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} WIB · Sumber: {data.source}
          </small>
        </div>
      </header>

      <section className="print-section" data-testid="print-map-section">
        <h2>1. Peta Sebaran Titik Hotspot (Hari Ini)</h2>
        <div className="print-map">
          <MapPanel
            hotspots={hotspots}
            kalbarHotspots={kalbarHotspots}
            districts={districtsWithCounts}
            reports={[]}
            selectedDistrict="Semua wilayah"
          />
        </div>
        <p className="print-caption">
          {hotspots.length} titik hotspot Kayong Utara dipetakan · titik abu-abu = konteks Kalimantan Barat di luar kabupaten
        </p>
      </section>

      <section className="print-section" data-testid="print-district-section">
        <h2>2. Ringkasan Wilayah · Titik Hotspot per Kecamatan</h2>
        <DistrictList
          districts={districtsWithCounts}
          selected="Semua wilayah"
          onSelect={() => {}}
          onReset={() => {}}
          totalHotspots={hotspots.length}
        />
      </section>

      <section className="print-section print-grid2" data-testid="print-trend24-section">
        <div className="trend-panel">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">TREN 24 JAM</span>
              <h3>Curah hujan &amp; gelombang</h3>
            </div>
            <div className="chart-key">
              <span><i className="rain-line" /> Hujan (mm)</span>
              <span><i className="wave-line" /> Gelombang (m)</span>
            </div>
          </div>
          <TrendChart data={data.hourly} />
        </div>
        <div className="alerts-panel">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">PERINGATAN TERKINI</span>
              <h3>Perlu perhatian</h3>
            </div>
            <Wind size={18} />
          </div>
          <AlertList alerts={data.alerts} />
        </div>
      </section>

      <section className="print-section print-grid2" data-testid="print-trend7-section">
        <HotspotTrend data={trend} />
        <IspuTrend data={ispuTrend.items} districts={ispuTrend.districts} />
      </section>

      <section className="print-section" data-testid="print-ispu-section">
        <h2>3. Grafik ISPU per Kecamatan · per Polutan (Permen LHK P.14/2020)</h2>
        <IspuDetail showAll />
      </section>

      <footer className="print-foot">
        SITAKO REALTIME · BPBD Kabupaten Kayong Utara · Koordinat WGS84 · Dokumen dihasilkan otomatis dari data live
      </footer>
    </main>
  );
}
